// components/FollowButton.tsx
import { Button } from '@/components/ui/extended-button'
import { useAppDispatch, useAppSelector } from 'features/hooks'
import { RootState } from 'features/store'
import {
  User,
  Listing,
  useFollowUserMutation,
  useUnFollowUserMutation,
  useFollowerFollowingLazyQuery,
} from 'graphql/generated/graphql'
import { useToast } from '@/hook/use-toast'
import { useRouter } from 'next/navigation'
import { setFollowerFOllowing, setPendingFollow } from 'features/authSlice'
import { useEffect, useState } from 'react'
import { useAuth } from '@/lib/authContext'
import { UnfollowModal } from './unfollow-modal'


interface FollowButtonProps {
  user: User | Listing
  onFollow?: (user: User | Listing) => void
  onUnfollow?: (user: User | Listing) => void
}

export const FollowButton = ({ user }: FollowButtonProps) => {
  const { checkAuth } = useAuth()
  const { followerFollowing, user: authUser } = useAppSelector(
    (state: RootState) => state.culineerAuth
  )
  const [followUser] = useFollowUserMutation()
  const [unFollowUser] = useUnFollowUserMutation()
  const [
    getFollowerFollowing,
    { loading: followerFollowingLoading, refetch: refetchFollowers },
  ] = useFollowerFollowingLazyQuery({ fetchPolicy: 'no-cache' })
  const dispatch = useAppDispatch()
  const { toast, toasts } = useToast()
  const router = useRouter()
  const [showUnfollowModal, setShowUnfollowModal] = useState(false)

  useEffect(() => {
    if (!followerFollowing) {
      refetchFollowerFollowing()
    }
  }, [followerFollowing])

  const isMarket = user?.user_type?.toLowerCase() === 'market'
  const userId = 'user_id'
  const displayName = (user as any)?.full_name || (user as any)?.name || 'this user'
  const displayPhoto = (user as any)?.profile_photo_resource || (user as any)?.photo

  const checkIsFollowing = (authUser: User | Listing) => {
    return (
      followerFollowing?.MyFollowing.results.some(
        (user) => user.user_id === authUser[userId]
      ) || false
    )
  }

  // @ts-ignore
  const isFollowing = checkIsFollowing(user)
  const isSelf = !!authUser?.user_id && user?.[userId] === authUser.user_id

  const refetchFollowerFollowing = async () => {
    const followerFollowingResponse = await getFollowerFollowing({
      variables: { userId: authUser.user_id, limit: 1000, offset: 1, searchTerm: '' },
    })

    if (followerFollowingResponse.data) {
      dispatch(
        setFollowerFOllowing(
          // @ts-ignore
          followerFollowingResponse.data
        )
      )
    }
  }

  const checkUserType = () => {
    const userType = user?.user_type?.toLowerCase()
    const id = user['slug'];
    
    if (!id) return
    
    if (userType === 'market') {
      router.push(`/market/${id}`)
    } else if (userType === 'producer') {
      router.push(`/producer/${id}`)
    } else {
      router.push(`/member/${id}`)
    }
  }
  

  const handleFollow = async (e) => {
    if (!checkAuth({ name: displayName, userType: user?.user_type })) {
      dispatch(setPendingFollow({ targetId: user[userId], targetType: user?.user_type }))
      return
    }
    e.stopPropagation()

    try {
      // Attempt to follow the user
      const result = await followUser({ variables: { userID: user[userId] } })

      // Check if the follow operation was successful
      if (result.data.follow) {
        // Refetch follower/following data
        refetchFollowerFollowing()

        // Display success toast
        toast({
          title: `Following ${displayName}`,
          description: (
            <span
            onClick={() => checkUserType()}
              className="text-muted-foreground"
            >
              You are now following {displayName}
            </span>
          ),
          image: displayPhoto,
        })
      } else {
        // Handle the case where the user is already following
        toast({
          title: 'Already Following',
          description: `You are already following ${displayName}.`,
        })
      }
    } catch (error) {
      // Handle Apollo errors
      console.error('Error following user:', error)

      // Extract the error message from the Apollo error
      let errorMessage = 'An error occurred while trying to follow the user.'

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        // GraphQL errors
        errorMessage = error.graphQLErrors.map((err) => err.message).join(', ')
      } else if (error.networkError) {
        // Network errors
        errorMessage = error.networkError.message || 'Network error occurred.'
      } else {
        // Generic error
        errorMessage = error.message
      }

      // Display error toast
      toast({
        title: 'Following',
        description: errorMessage,
      })
    }
  }

  const handleUnfollow = (e) => {
    if (!checkAuth()) return
    e.stopPropagation()
    setShowUnfollowModal(true)
  }

  const confirmUnfollow = async () => {
    try {
      const result = await unFollowUser({ variables: { userID: user[userId] } })
      if (result.data.unFollow) {
        refetchFollowerFollowing()
        toast({
          title: `Unfollowed ${displayName}`,
          description: `You have unfollowed ${displayName}.`,
          image: displayPhoto,
        })
      } else {
        toast({
          title: 'Unfollow',
          description: `Failed to unfollow ${displayName}.`,
        })
      }
    } catch (error) {
      let errorMessage = 'An error occurred while trying to unfollow the user.'
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors.map((err) => err.message).join(', ')
      } else if (error.networkError) {
        errorMessage = error.networkError.message || 'Network error occurred.'
      } else {
        errorMessage = error.message
      }
      toast({
        title: 'Unfollow',
        description: errorMessage,
      })
    } finally {
      setShowUnfollowModal(false)
    }
  }

  if (isSelf) return null

  return (
    <>
      {isFollowing ? (
        <>
          <Button
            variant="default"
            size="xs"
            className="bg-[#F7F7F7] px-[29px] py-[20px] text-[16px] font-medium text-black hover:bg-[#F7F7F7]"
            onClick={handleUnfollow}
          >
            Following
          </Button>
          <UnfollowModal
            open={showUnfollowModal}
            onClose={() => setShowUnfollowModal(false)}
            onConfirm={confirmUnfollow}
            userName={displayName}
          />
        </>
      ) : (
        <Button
          variant="default"
          size="xs"
          className="bg-[#85C349] px-[29px] py-[20px] text-[16px] font-semibold hover:bg-[#548828]"
          onClick={handleFollow}
        >
          Follow
        </Button>
      )}
    </>
  )
}
